import { useEffect, useRef, useState } from 'react';
import { Autocomplete, Chip, Divider, Grid, MenuItem, Select, TextField } from '@mui/material';

import { useAppDispatch } from '../../../store/hooks';
import { fetchLeadSources, fetchLeadSubSources, fetchUnofyCrmAgents } from '../../../store/accountThunk';
import useModuleAccess from '../../../hooks/useModuleAccess';
import { PlanLocked } from '../../Subscription/SubscriptionGate';

export interface CrmLeadValue {
  sourceRef?: string;
  sourceLabel?: string;
  subSourceRef?: string;
  subSourceLabel?: string;
  /** What the invoice stores as its `source`: the sub-source, or the source when it has none. */
  sourceValue?: string;
  agentId?: string;
  agentName?: string;
}

/** The attribution the imported CRM lead already carries, as free-text labels. */
export interface CrmLeadDefaults {
  leadId?: string;
  sourceName?: string;
  subSourceName?: string;
  agentName?: string;
}

/** A buyer's saved `crmLead`; labels are resolved by the section once masters load. */
export const fromPartyLead = (lead: any): CrmLeadValue => ({
  sourceRef: lead?.sourceRef?._id || lead?.sourceRef || '',
  subSourceRef: lead?.subSourceRef?._id || lead?.subSourceRef || '',
  agentId: lead?.agentId || '',
  agentName: lead?.agentName || '',
});

/** Flat multipart fields the buyer create / update endpoints fold into `crmLead`. */
export const toPartyLeadFields = (value: CrmLeadValue) => ({
  crmLeadSourceRef: value.sourceRef || '',
  crmLeadSubSourceRef: value.subSourceRef || '',
  crmLeadAgentId: value.agentId || '',
  crmLeadAgentName: value.agentName || '',
});

interface LeadProps {
  value: CrmLeadValue;
  defaults?: CrmLeadDefaults;
  onChange: (value: CrmLeadValue) => void;
  /** Read-only like the rest of a picked party, until Edit. */
  readOnly?: boolean;
  /** Show required errors; set once the form has been submitted. */
  showErrors?: boolean;
}

/** Sub source and agent are required whenever the section is shown. */
export const isLeadComplete = (value: CrmLeadValue) => !!(value.subSourceRef && value.agentId);

// Same borderless dropdown look the Legal section uses.
const selectSx = {
  width: '100%',
  boxShadow: 'none',
  textAlign: 'right',
  '& .MuiOutlinedInput-input': { p: 0.5 },
  marginBottom: '3px',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
};

const sameLabel = (a?: string, b?: string) =>
  !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * Lead attribution section of the buyer form: which source/sub-source brought the
 * buyer in and which CRM agent handled them. Saved on the party as `crmLead`; the
 * invoice pre-fills from it and forwards it to the CRM.
 */
const Lead = ({ value, defaults, onChange, readOnly, showErrors }: LeadProps) => {
  const dispatch = useAppDispatch();
  const locked = !useModuleAccess().planAllowsModule('crm');

  const [sources, setSources] = useState<any[]>([]);
  const [subSources, setSubSources] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Seed from the imported lead's labels once, never over a user's own pick.
  const seeded = useRef(false);

  useEffect(() => {
    if (locked) return;
    const load = async () => {
      try {
        const res: any = await dispatch(fetchLeadSources()).unwrap();
        setSources(res?.data || []);
      } catch (error) {
        console.error('Error fetching lead sources:', error);
      }
      try {
        const res: any = await dispatch(fetchLeadSubSources()).unwrap();
        setSubSources(res?.data || []);
      } catch (error) {
        console.error('Error fetching lead sub-sources:', error);
      }
      try {
        const res: any = await dispatch(fetchUnofyCrmAgents()).unwrap();
        setAgents(res?.data || []);
      } catch (error) {
        console.error('Error fetching CRM agents:', error);
      }
      setLoaded(true);
    };
    load();
  }, [dispatch, locked]);

  const build = (subSourceId: string, sourceId: string, agentId: string): CrmLeadValue => {
    const sub = subSources.find((s: any) => s._id === subSourceId);
    const src = sources.find((s: any) => s._id === sourceId);
    const agent = agents.find((a: any) => a.id === agentId);
    const subSourceLabel = sub ? sub.label : '';
    const sourceLabel = src ? src.label : '';
    return {
      sourceRef: sourceId,
      sourceLabel,
      subSourceRef: subSourceId,
      subSourceLabel,
      // A source with no sub-sources (or none picked) is itself the attribution.
      sourceValue: subSourceLabel || sourceLabel,
      agentId,
      // Keep a saved agent's name even if the CRM no longer lists them.
      agentName: agent ? agent.name : (agentId && agentId === value.agentId ? value.agentName || '' : ''),
    };
  };

  const hasLead = !!(value.subSourceRef || value.sourceRef || value.agentId);

  // A saved lead comes as ids only; resolve its labels for the invoice. Re-runs when
  // the party is reloaded after an Edit.
  useEffect(() => {
    if (!loaded || !hasLead) return;
    onChange(build(value.subSourceRef || '', value.sourceRef || '', value.agentId || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, value.subSourceRef, value.sourceRef, value.agentId]);

  useEffect(() => {
    if (seeded.current || !loaded) return;
    seeded.current = true;
    if (hasLead || !defaults) return;
    // The imported lead carries labels, not our master ids, so match them by name.
    const sub = subSources.find((s: any) => sameLabel(s.label, defaults.subSourceName));
    const src = sub?.source?._id
      ? sub.source._id
      : sources.find((s: any) => sameLabel(s.label, defaults.sourceName))?._id;
    const agent = agents.find((a: any) => sameLabel(a.name, defaults.agentName));
    if (sub || src || agent) onChange(build(sub?._id || '', src || '', agent?.id || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, defaults]);

  return (
    <>
      <Grid item xs={12} mt={1}>
        <Divider>
          <Chip label="Lead Details" size="small" />
        </Divider>
      </Grid>

      {locked && <Grid item xs={12}><PlanLocked /></Grid>}

      {!locked && <>
      {defaults?.leadId &&
        <Grid item xs={12}>
          <h5 className='c-label'>Lead ID</h5>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#488dff' }}>{defaults.leadId}</span>
        </Grid>}

      <Grid item xs={4}>
        <h5 className='c-label'>Sub Source*</h5>
        <Autocomplete
          size='small'
          readOnly={readOnly}
          options={subSources}
          value={subSources.find((s: any) => s._id === value.subSourceRef) || null}
          onChange={(event, option: any) =>
            onChange(build(option ? option._id : '', option?.source?._id || '', value.agentId || ''))}
          getOptionLabel={(option: any) => option.label || ''}
          isOptionEqualToValue={(option: any, val: any) => option._id === val._id}
          renderOption={(props, option: any) => (
            <li {...props} key={option._id} style={{ fontSize: 14 }}>{option.label}</li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              className='c-text-field'
              variant="standard"
              placeholder={subSources.length ? 'Select Sub Source' : 'No sub sources'}
              InputProps={{ ...params.InputProps, disableUnderline: true }}
            />
          )}
        />
        {showErrors && !readOnly && !value.subSourceRef &&
          <p style={{color: "#c33b3b"}}>Sub Source is required.</p>}
      </Grid>

      <Grid item xs={4}>
        <h5 className='c-label'>Source</h5>
        {/* Always follows the picked sub-source. */}
        <Select
          className='c-text-field'
          displayEmpty
          disabled
          size='small'
          variant='outlined'
          sx={selectSx}
          value={value.sourceRef || ''}
        >
          <MenuItem value='' key='none'>Select Sub Source first</MenuItem>
          {sources.map((option: any) => (
            <MenuItem value={option._id} key={option._id}>{option.label}</MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={4}>
        <h5 className='c-label'>Agent*</h5>
        <Autocomplete
          size='small'
          readOnly={readOnly}
          options={agents}
          value={agents.find((a: any) => a.id === value.agentId) || null}
          onChange={(event, option: any) =>
            onChange(build(value.subSourceRef || '', value.sourceRef || '', option ? option.id : ''))}
          getOptionLabel={(option: any) => option.name || ''}
          isOptionEqualToValue={(option: any, val: any) => option.id === val.id}
          renderOption={(props, option: any) => (
            <li {...props} key={option.id} style={{ fontSize: 14 }}>{option.name}</li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              className='c-text-field'
              variant="standard"
              placeholder={agents.length ? 'Select Agent' : 'No agents'}
              InputProps={{ ...params.InputProps, disableUnderline: true }}
            />
          )}
        />
        {showErrors && !readOnly && !value.agentId &&
          <p style={{color: "#c33b3b"}}>Agent is required.</p>}
      </Grid>
      </>}
    </>
  );
};

export default Lead;
